/**
 * The reproductions overview: every reproduction of every kind (logo, image,
 * gradient) as a card that names its kind, filtered by type and project
 * (client-side on the full list) and by the Mine/All toggle (server-side).
 *
 * Reproductions are team-wide; the Mine/All toggle narrows the list to the
 * account's own through `?scope=`. The card is ReproductionCard, shared with
 * the Projects page; the folder button in its header files it in a project.
 * New reproductions start on the type choice page.
 */
import { useNavigate } from "react-router-dom";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Alert } from "@mui/material";
import ResourcePanel, { PanelButton, PanelClearFilters, PanelScopeRow } from "../../components/ResourcePanel";
import ResourcePageLayout from "../../components/ResourcePageLayout";
import { ITEM_KIND_STYLES } from "../../components/itemKindStyles";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { deleteRights, useDeleteRights } from "../../helper/deleteRights";
import {
  REPRODUCTION_KINDS, REPRODUCTION_KIND_SLUGS, REPRODUCTION_TYPE_CHOICE_ROUTE, reproductionRoute,
} from "../../helper/reproductionFlow";
import { downloadReproductionSource } from "../../helper/reproductionSource";
import { LIST_SCOPE_MINE, useListScope, withListScope } from "../../components/ListScopeToggle";
import ReproductionCard from "../../components/ReproductionCard";
import ProjectFilterSelect, { ALL_PROJECTS, matchesProjectFilter } from "../../components/ProjectFilterSelect";
import KindFilterSelect, { ALL_KINDS } from "../../components/KindFilterSelect";
import useProjects from "../../components/useProjects";
import useReproductionThumbnails from "../../components/useReproductionThumbnails";
import { moveItems, projectErrorMessage } from "../../helper/projects";

/** The type dropdown's entries: every reproduction kind, by its reproduction_type value. */
/** The reproductions' tones: the panel band and its New button share them. */
const KIND = ITEM_KIND_STYLES.reproduction;
const KIND_BUTTON_SX = { bgcolor: KIND.header, "&:hover": { bgcolor: KIND.header, filter: "brightness(0.9)" } };

const TYPE_OPTIONS = REPRODUCTION_KIND_SLUGS.map((slug) => ({
  value: REPRODUCTION_KINDS[slug].type, label: REPRODUCTION_KINDS[slug].navLabel,
}));

/** A row without a reproduction_type predates the kinds: a logo. */
const typeOf = (row) => row.reproduction_type || "LOGO_REPRODUCTION";

function ColorReproductionHome() {
  const navigate = useNavigate();
  const [pageState, setPageState] = useState({ isLoading: false, data: [] });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [deleteNameInput, setDeleteNameInput] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [scope, setScope] = useListScope();
  const [projectFilter, setProjectFilter] = useState(ALL_PROJECTS);
  const [typeFilter, setTypeFilter] = useState(ALL_KINDS);
  // Whether the signed-in account may delete a row: its creator or an
  // administrator (who is warned in the dialog). Same rule on the server.
  const me = useDeleteRights();
  const rightsOf = (row) => deleteRights(row, me, "reproduction");
  const currentRights = currentRow ? rightsOf(currentRow) : null;
  const { projects, reload: reloadProjects } = useProjects();
  // Small rendered preview of each reproduction - what gets marked.
  const { thumbnails, load: loadThumbnails } = useReproductionThumbnails();

  const fetchData = async () => {
    setPageState((old) => ({ ...old, isLoading: true }));
    try {
      // ?scope= narrows the team-wide list to the account's own (Mine/All).
      const res = await axios.get(
        withListScope("api/reproduction/get_existing_reproductions", scope)
      );
      const rows = res.data || [];
      setPageState((old) => ({ ...old, data: rows }));
      loadThumbnails(rows);
    } catch (err) {
      console.error("Error fetching reproductions:", err);
      setPageState((old) => ({ ...old, data: [] }));
      setErrorMessage("Loading the reproductions failed.");
    } finally {
      setPageState((old) => ({ ...old, isLoading: false }));
    }
  };

  // Re-fetch when the Mine/All toggle changes (owner filter is server-side,
  // the type and project filters client-side on the same list).
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Opens the reproduction's page: the URL carries its kind and id. Final
  // (the Marking step was entered): only that step is open.
  const onOpenClick = (row) => {
    navigate(reproductionRoute(typeOf(row), row.id, row.setup_final ? "marking" : "setup"));
  };

  // The uploaded input file (SVG / image / shape) of a card's reproduction.
  const [downloadingId, setDownloadingId] = useState(null);
  const handleDownloadSource = async (row) => {
    setDownloadingId(row.id);
    try {
      await downloadReproductionSource(row.id, row.original_file_name);
    } catch (error) {
      console.error("Error downloading the input file:", error);
      setErrorMessage("Downloading the input file failed.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOpenDeleteDialog = (row) => {
    setCurrentRow(row);
    setDeleteNameInput("");
    setDeleteConfirmOpen(true);
  };
  const handleCloseDeleteDialog = () => {
    setDeleteConfirmOpen(false);
    setDeleteNameInput("");
  };
  const deleteArmed = currentRow && deleteNameInput.trim() === currentRow.name;
  const handleConfirmDelete = async () => {
    if (currentRow && deleteArmed) {
      try {
        await axios.delete(`api/reproduction/delete/${currentRow.id}/`);
        handleCloseDeleteDialog();
        fetchData();
      } catch (error) {
        // 403: not the creator - the backend names the rule.
        console.error("Error deleting reproduction:", error);
        handleCloseDeleteDialog();
        setErrorMessage(
          error?.response?.data?.error || "Deleting the reproduction failed.",
        );
      }
    }
  };

  // Files the reproduction in a project (null = unfiled).
  const handleMoveToProject = async (row, projectId) => {
    try {
      await moveItems([{ kind: "reproduction", id: row.id }], projectId);
      const name = projectId ? projects.find((p) => p.id === projectId)?.name || null : null;
      setPageState((old) => ({
        ...old,
        data: old.data.map((r) => (r.id === row.id ? { ...r, project: projectId, project_name: name } : r)),
      }));
      reloadProjects();
    } catch (error) {
      console.error("Moving the reproduction failed:", error);
      setErrorMessage(projectErrorMessage(error, "Moving the reproduction failed."));
    }
  };

  const filteredData = pageState.data.filter(
    (row) =>
      (typeFilter === ALL_KINDS || typeOf(row) === typeFilter) &&
      matchesProjectFilter(row, projectFilter)
  );
  const filtered = projectFilter !== ALL_PROJECTS || typeFilter !== ALL_KINDS;

  const total = pageState.data.length;
  const panel = (
    <ResourcePanel
      bandSx={{ background: KIND.header }}
      overline={scope === LIST_SCOPE_MINE ? "Your reproductions" : "All reproductions"}
      // One count, in the panel only: what the list shows, "of" the total
      // while a filter narrows it.
      headline={
        pageState.isLoading
          ? "Loading"
          : filtered
            ? `${filteredData.length} of ${total} reproductions`
            : `${total} reproduction${total === 1 ? "" : "s"}`
      }
      subline={scope === LIST_SCOPE_MINE ? "Created by you" : "All accounts"}
      about="A reproduction marks a logo, an image or a gradient with a palette: the colours are assigned, the design is placed on the plate and the marking file is generated."
      filters={
        <>
          <PanelScopeRow value={scope} onChange={setScope} />
          <KindFilterSelect label="Type" options={TYPE_OPTIONS} value={typeFilter} onChange={setTypeFilter} sx={{ minWidth: 0, width: "100%" }} />
          <ProjectFilterSelect projects={projects} value={projectFilter} onChange={setProjectFilter} sx={{ minWidth: 0, width: "100%" }} />
          {filtered ? (
            <PanelClearFilters
              onClick={() => {
                setTypeFilter(ALL_KINDS);
                setProjectFilter(ALL_PROJECTS);
              }}
            />
          ) : null}
        </>
      }
      actions={
        <>
          <PanelButton primary sx={KIND_BUTTON_SX} startIcon={<AddCircleOutlineIcon />} onClick={() => navigate(REPRODUCTION_TYPE_CHOICE_ROUTE)}>
            New reproduction
          </PanelButton>
          <PanelButton
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchData();
              reloadProjects();
            }}
            disabled={pageState.isLoading}
          >
            Refresh
          </PanelButton>
        </>
      }
    />
  );

  return (
    <>
      <ResourcePageLayout
        title="Reproductions"
        loading={pageState.isLoading}
        loadingLabel="Loading reproductions…"
        error={
          errorMessage ? (
            <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          ) : null
        }
        count={filteredData.length}
        emptyText={
          filtered
            ? "No reproductions match the selected filters."
            : scope === LIST_SCOPE_MINE
              ? "You have no reproductions yet — create one."
              : "No reproductions yet — create the first one."
        }
        panel={panel}
        grid
      >
        {filteredData.map((row) => (
          <ReproductionCard
            key={row.id}
            row={row}
            rights={rightsOf(row)}
            thumbnail={thumbnails[row.id]}
            onOpen={() => onOpenClick(row)}
            onDownload={() => handleDownloadSource(row)}
            downloading={downloadingId === row.id}
            onDelete={() => handleOpenDeleteDialog(row)}
            projects={projects}
            onMoveToProject={(projectId) => handleMoveToProject(row, projectId)}
          />
        ))}
      </ResourcePageLayout>

      {/* Delete confirmation: type the reproduction name to arm the button. */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete reproduction?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes the reproduction “{currentRow?.name}” with its
            color assignments and marking file. This action cannot be undone.
          </DialogContentText>
          {currentRights?.warning && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {currentRights.warning}
            </Alert>
          )}
          <TextField
            fullWidth
            autoFocus
            size="small"
            sx={{ mt: 2 }}
            label="Type the reproduction name to confirm"
            placeholder={currentRow?.name || ""}
            value={deleteNameInput}
            onChange={(event) => setDeleteNameInput(event.target.value)}
            error={deleteNameInput.length > 0 && !deleteArmed}
            helperText={
              deleteNameInput.length > 0 && !deleteArmed
                ? "Does not match the reproduction name"
                : "Enter the exact name to enable deletion"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={!deleteArmed}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ColorReproductionHome;
